import React, { useState, useEffect } from 'react';
import PendingInviteAPI from 'api/PendingInviteAPI';
import EmailAPI from 'api/EmailAPI';
import UsersAPI from 'api/UsersAPI';
import { inviteUserNotification } from 'utils/NotificationsFuncs';
import { useSnackbar } from 'notistack';
import InviteModal from '../../components/Profile/InviteModal';
import AdminTeamViewList from './AdminTeamViewList';
import { LicenseType } from 'pages/teams/LicenseBadge';
import { buildLicenseInventory } from 'utils/licenseInventory';

interface IAdminInvites {
  curTeam: any;
  customerInfo: any;
}

interface PendingInviteRecord {
  email: string;
  preassigned_license_type: LicenseType;
}

const resolveLicenseType = (rawLicense: string): LicenseType => {
  const normalized = String(rawLicense ?? '').toLowerCase();
  if (normalized === 'starter') {
    return 'pro';
  }
  if (normalized === 'tariffs') {
    return 'standard';
  }
  if (
    normalized === 'standard' ||
    normalized === 'pro' ||
    normalized === 'business' ||
    normalized === 'enterprise'
  ) {
    return normalized;
  }
  return 'none';
};

const AdminInvites: React.FC<IAdminInvites> = ({ curTeam, customerInfo }) => {
  const [pending, setPending] = useState<PendingInviteRecord[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const [inviteMode, setInviteMode] = useState<boolean>(false);
  const [searchVal, setSearchVal] = useState('');

  const validateEmail = (email: string): boolean => {
    // Simple, practical email regex:
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return Boolean(email) && isValidEmail.test(email);
  };

  const handleSubmit = (): void => {
    setPending((prevPending) => {
      const lowerCaseQuery = searchVal.toLowerCase();
      // Sort pending, placing matches first based on email
      const sortedPending = [...prevPending].sort((a, b): number => {
        const aMatches = [a?.email].some((field) =>
          // validate field is present, skips value otherwise
          field?.toLowerCase()?.includes(lowerCaseQuery),
        );
        const bMatches = [b?.email].some((field) =>
          // validate field is present, skips value otherwise
          field?.toLowerCase()?.includes(lowerCaseQuery),
        );

        if (aMatches && !bMatches) {
          return -1;
        } // a comes before b
        if (!aMatches && bMatches) {
          return 1;
        } // b comes before a
        return 0; // Keep original order if both match or both don't match
      });
      return sortedPending;
    });
  };

  const handleChange = (e: any) => {
    setSearchVal(e.target.value);
    if (!e.target.value) {
      handleReset();
    }
  };

  const handleReset = () => {
    PendingInviteAPI.fetchTeamPendingInvites(curTeam?.id, true)
      .then((res) => {
        if (res?.success) {
          setPending(
            (res?.result?.data ?? []).map((u: any) => ({
              email: String(u?.email ?? ''),
              preassigned_license_type: resolveLicenseType(
                u?.preassigned_license_type,
              ),
            })),
          );
        }
      })
      .catch(() => {
        enqueueSnackbar('Unable to fetch pending invites', {
          variant: 'error',
        });
      });
  };

  const sendInvite = async (formVals: any) => {
    const adminObj = {
      basics: {
        firstName: curTeam?.attributes?.team_admin[0],
        lastName: '',
      },
    };

    const inviteEntries = Array.isArray(formVals?.invites)
      ? formVals.invites
          .map((invite: any) => ({
            email: String(invite?.email ?? '').trim(),
            license: String(invite?.license ?? 'none').toLowerCase(),
          }))
          .filter((invite: { email: string }) => Boolean(invite.email))
      : Array.isArray(formVals?.emails)
      ? formVals.emails.map((email: string) => ({
          email: email.trim(),
          license: 'none',
        }))
      : [formVals?.email?.trim()]
          .filter(Boolean)
          .map((email: string) => ({ email: email, license: 'none' }));

    const emails = inviteEntries.map((entry: any) => entry.email);
    const hasInvalidEmail = emails.some(
      (email: string) => !validateEmail(email),
    );

    if (emails.length === 0 || hasInvalidEmail) {
      enqueueSnackbar('Email is invalid', { variant: 'error' });
      return false;
    }

    let successfulInvites = 0;

    for (const inviteEntry of inviteEntries) {
      const email = inviteEntry.email;
      try {
        const res = await EmailAPI.inviteUserToTeam({
          requestorEmail: curTeam?.attributes?.team_admin[0],
          teamID: curTeam?.id,
          teamName: curTeam?.name,
          formValues: { email: email },
        });

        if (!res?.success) {
          const message: string =
            res && res.result && typeof res.result === 'string'
              ? res.result
              : `Unable to send team invite to ${email}`;
          enqueueSnackbar(message, { variant: 'error' });
          continue;
        }

        const res2 = await PendingInviteAPI.addPendingInvite(
          curTeam?.id,
          email.toLowerCase(),
          inviteEntry.license,
        );
        if (res2?.success) {
          successfulInvites += 1;
        }

        const res3 = await UsersAPI.searchUsers(email);
        if (res3?.success) {
          if (res3.result.length > 0) {
            const userId = res3.result[0].id;
            await inviteUserNotification(adminObj, curTeam, userId);
          }
        } else {
          enqueueSnackbar('Unable to send invite notification to user', {
            variant: 'error',
          });
        }
      } catch {
        enqueueSnackbar(`Unable to send team invite to ${email}`, {
          variant: 'error',
        });
      }
    }

    if (successfulInvites > 0) {
      enqueueSnackbar(
        successfulInvites === 1
          ? 'Team invite sent via Email'
          : `${successfulInvites} team invites sent via Email`,
        { variant: 'success' },
      );
      history.go(0);
      return true;
    }

    enqueueSnackbar('Unable to send team invite via Email', {
      variant: 'error',
    });
    return false;
  };

  const handleResend = async (email: string) => {
    const adminObj = {
      basics: {
        firstName: curTeam?.attributes?.team_admin[0],
        lastName: '',
      },
    };
    await EmailAPI.inviteUserToTeam({
      requestorEmail: curTeam?.attributes?.team_admin[0],
      teamID: curTeam?.id,
      teamName: curTeam?.name,
      formValues: { email: email },
    })
      .then(async (res) => {
        if (res?.success) {
          const res3 = await UsersAPI.searchUsers(email);
          if (res3?.success) {
            if (res3.result.length > 0) {
              const userId = res3.result[0].id;
              await inviteUserNotification(adminObj, curTeam, userId);
              enqueueSnackbar('User received notifaction to join your team', {
                variant: 'success',
              });
            }
          } else {
            enqueueSnackbar('Unable to send invite notification to user', {
              variant: 'error',
            });
          }
        } else {
          const message: string =
            res && res.result && typeof res.result === 'string'
              ? res.result
              : 'Unable to send team invite via Email';
          enqueueSnackbar(message, { variant: 'error' });
        }
      })
      .catch(() => {
        enqueueSnackbar('Unable to send team invite via Email', {
          variant: 'error',
        });
      });
  };

  const handleRevoke = async (email: string) => {
    const res = await PendingInviteAPI.removePendingInvite(curTeam.id, email);
    if (res?.success) {
      enqueueSnackbar('User invite has been revoked', { variant: 'success' });
      history.go(0);
    } else {
      enqueueSnackbar('Unable to revoke invite at this time', {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    // remove empty placeholder pending value
    PendingInviteAPI.fetchTeamPendingInvites(curTeam?.id, true)
      .then((res) => {
        if (res?.success) {
          setPending(
            (res?.result?.data ?? []).map((u: any) => ({
              email: String(u?.email ?? ''),
              preassigned_license_type: resolveLicenseType(
                u?.preassigned_license_type,
              ),
            })),
          );
        }
      })
      .catch(() => {
        enqueueSnackbar('Unable to fetch pending invites', {
          variant: 'error',
        });
      });
  }, [curTeam]);

  const licenseInventory: Record<LicenseType, { total: number; used: number }> =
    React.useMemo(() => {
      const inventory: Record<LicenseType, { total: number; used: number }> = {
        standard: { total: 0, used: 0 },
        pro: { total: 0, used: 0 },
        business: { total: 0, used: 0 },
        enterprise: { total: 0, used: 0 },
        none: { total: Infinity, used: 0 },
      };

      const subscriptions = Array.isArray(
        customerInfo?.data?.subscriptions?.data,
      )
        ? customerInfo.data.subscriptions.data
        : [];

      const licenseTotals = buildLicenseInventory(subscriptions);
      inventory.standard.total = licenseTotals.standard;
      inventory.pro.total = licenseTotals.pro;
      inventory.business.total = licenseTotals.business;
      inventory.enterprise.total = licenseTotals.enterprise;

      return inventory;
    }, [customerInfo?.data?.subscriptions?.data]);

  return (
    <div className='team-content-container'>
      <div className='team-content-header'>
        <div className='team-content-search'>
          <div className='screening-input-field-container'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
            >
              <path
                d='M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z'
                stroke='var(--card-text)'
                strokeWidth='1.66667'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <input
              placeholder='Search users'
              id='primary'
              type='text'
              className='screening-input-field-content'
              name='primary'
              value={searchVal}
              onChange={(e) => handleChange(e)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
            />
          </div>
        </div>
        <div
          className='team-content-invite-user'
          onClick={() => setInviteMode(true)}
        >
          <div
            className='team-content-invite-user-text'
            style={{ color: '#fff' }}
          >
            + Invite User
          </div>
        </div>
      </div>
      <div className='team-content-table'>
        <div
          style={{
            marginBottom: '20px',
            marginLeft: '20px',
            display: 'flex',
            justifyContent: 'row',
            gap: '20px',
          }}
        ></div>
        <div className='team-table-body-header'>
          <div className='team-table-body-name-column'>
            <div className='team-table-body-column-inner'>
              <div className='team-table-body-column-text'>Name</div>
            </div>
          </div>
          <div className='team-table-body-name-column'>
            <div className='team-table-body-column-inner'>
              <div className='team-table-body-column-text'>Email</div>
            </div>
          </div>
          <div className='team-table-body-perms-column'>
            <div className='team-table-body-column-inner'>
              <div className='team-table-body-column-text'>
                Pre-assigned License
              </div>
            </div>
          </div>
          <div className='team-table-body-placeholder-column'></div>
        </div>
        <AdminTeamViewList
          pendingList={pending}
          handleResend={handleResend}
          handleRevoke={handleRevoke}
        />
      </div>
      <InviteModal
        open={inviteMode}
        onClose={() => setInviteMode(false)}
        setInviteMode={setInviteMode}
        sendInvite={sendInvite}
        licenseInventory={licenseInventory}
        existingPendingInvites={pending}
      />
    </div>
  );
};

export default AdminInvites;
